import { translate } from '../../../helpers/translations/translator'
import { DepartmentFormData } from '../../entities/Department/Department'
import {
    getDepartmentsIndexCall,
    patchDepartmentApiCall,
    patchSoftDeleteDepartmentApiCall,
    postDepartmentApiCall,
} from '../../entities/Department/DepartmentService'
import { isFetchCollectionResultSuccessful, isFetchResultSuccessful } from '../../entities/FetchResult'
import { NotificationCenterAction, NotificationCenterActionCategory, NotificationCenterCategory } from '../../entities/NotificationCenter/NotificationCenter'
import { endEventLoggerApiCall, startEventLoggerApiCall } from '../../entities/NotificationCenter/NotificationCenterService'
import trans from '../../helpers/trans'
import { ToastType } from '../../types'
import { Reducers } from '../reducers'
import { TypedDispatch } from '../store'
import { setToast } from '../toasts/toastsReducer'
import {
    departmentAdded,
    departmentDeleted,
    departmentPatched,
    removeLoadingDepartment,
    setDepartments,
    setError,
    setIsLoading,
    setLoadingDepartment,
} from './departmentsReducer'

export const getDepartments = () => async (dispatch: TypedDispatch): Promise<void> => {
    dispatch(setIsLoading(true))

    try {
        const response = await getDepartmentsIndexCall()

        if (! isFetchCollectionResultSuccessful(response)) {
            dispatch(setError(response.error))

            return
        }

        dispatch(setDepartments(response.data))
    } catch (error) {
        console.error('[getDepartments]', error)
    } finally {
        dispatch(setIsLoading(false))
    }
}

export const addDepartment = (
    data: DepartmentFormData,
) => async (dispatch: TypedDispatch, getState: () => Reducers): Promise<void> => {
    const temporaryId = `temp-${crypto.randomUUID()}`
    const departmentGroup = getState().departmentGroupsReducer.departmentGroups.find(departmentGroup => departmentGroup.id === data.groupId)

    dispatch(setLoadingDepartment(temporaryId))
    dispatch(departmentAdded({
        id: temporaryId,
        name: data.name,
        departmentGroup,
    }))

    const responseTracker = await startEventLoggerApiCall(
        NotificationCenterCategory.DEPARTMENT,
        NotificationCenterAction.CREATED,
        [NotificationCenterActionCategory.UNKNOWN],
    )

    if (! isFetchResultSuccessful(responseTracker)) {
        dispatch(setError(responseTracker.error))
        dispatch(departmentDeleted(temporaryId))
        dispatch(removeLoadingDepartment(temporaryId))

        return
    }

    try {
        const response = await postDepartmentApiCall(data, responseTracker.data)

        if (! isFetchResultSuccessful(response)) {
            dispatch(setError(response.error))
            dispatch(departmentDeleted(temporaryId))
            dispatch(removeLoadingDepartment(temporaryId))

            return
        }

        dispatch(departmentDeleted(temporaryId))
        dispatch(departmentAdded(response.data))

        dispatch(setToast({
            text: translate('pages.management.addDepartmentSuccess'),
            type: ToastType.pass,
            shouldFadeOutAutomatically: true,
        }))
    } catch (error) {
        console.error('[addDepartment]', error)

        dispatch(departmentDeleted(temporaryId))
        dispatch(setError(trans('errors.unknownError')))
    } finally {
        await endEventLoggerApiCall(responseTracker.data)
        dispatch(removeLoadingDepartment(temporaryId))
    }
}

export const editDepartment = (
    departmentId: string,
    data: Partial<DepartmentFormData>,
) => async (dispatch: TypedDispatch, getState: () => Reducers): Promise<void> => {
    const originalDepartment = getState().pacoDepartmentsReducer.departments.find(department => department.id === departmentId)
    const departmentGroup = getState().departmentGroupsReducer.departmentGroups.find(departmentGroup => departmentGroup.id === data.groupId)

    dispatch(setLoadingDepartment(departmentId))
    dispatch(departmentPatched({
        id: departmentId,
        name: data.name,
        departmentGroup,
    }))

    const responseTracker = await startEventLoggerApiCall(
        NotificationCenterCategory.DEPARTMENT,
        NotificationCenterAction.UPDATED,
        [NotificationCenterActionCategory.UNKNOWN],
    )

    if (! isFetchResultSuccessful(responseTracker)) {
        dispatch(setError(responseTracker.error))
        dispatch(removeLoadingDepartment(departmentId))
        dispatch(departmentPatched(originalDepartment ?? {}))

        return
    }

    try {
        const response = await patchDepartmentApiCall(data, departmentId, responseTracker.data)

        console.log(response)

        if (! isFetchResultSuccessful(response)) {
            dispatch(setError(response.error))
            dispatch(removeLoadingDepartment(departmentId))
            dispatch(departmentPatched(originalDepartment ?? {}))

            return
        }

        dispatch(departmentPatched(response.data))

        dispatch(setToast({
            text: translate('pages.management.editDepartmentSuccess'),
            type: ToastType.pass,
            shouldFadeOutAutomatically: true,
        }))
    } catch (error) {
        console.error('[editDepartment]', error)

        dispatch(departmentPatched(originalDepartment ?? {}))
        dispatch(setError(trans('errors.unknownError')))
    } finally {
        await endEventLoggerApiCall(responseTracker.data)
        dispatch(removeLoadingDepartment(departmentId))
    }
}

export const deleteDepartment = (departmentId: string) => async (dispatch: TypedDispatch): Promise<void> => {
    dispatch(setLoadingDepartment(departmentId))

    const responseTracker = await startEventLoggerApiCall(
        NotificationCenterCategory.DEPARTMENT,
        NotificationCenterAction.DELETED,
        [NotificationCenterActionCategory.UNKNOWN],
    )

    if (! isFetchResultSuccessful(responseTracker)) {
        dispatch(setError(responseTracker.error))
        dispatch(removeLoadingDepartment(departmentId))

        return
    }

    try {
        const response = await patchSoftDeleteDepartmentApiCall(departmentId, responseTracker.data)

        if (! isFetchResultSuccessful(response)) {
            dispatch(setError(response.error))
            dispatch(removeLoadingDepartment(departmentId))

            return
        }

        dispatch(departmentPatched(response.data))
        dispatch(setToast({
            text: translate('pages.management.deleteDepartmentWarning'),
            type: ToastType.warning,
        }))
    } catch (error) {
        console.error('[deleteDepartment]', error)

        dispatch(setError(trans('errors.unknownError')))
    } finally {
        await endEventLoggerApiCall(responseTracker.data)
        dispatch(removeLoadingDepartment(departmentId))
    }
}